import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, X, Search, Pencil, Trash2, User } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import { facesApi, organizationsApi } from '../services/api';
import { useAuthStore } from '../services/auth';
import type { Face } from '../types';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

export default function Faces() {
  const { organizationId } = useAuthStore();
  const [selectedFace, setSelectedFace] = useState<Face | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  // Check if face recognition is enabled
  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationsApi.get(organizationId!),
    enabled: !!organizationId,
  });

  // Fetch faces
  const { data: faces, isLoading: facesLoading } = useQuery({
    queryKey: ['faces'],
    queryFn: () => facesApi.list(),
    enabled: !!organization?.face_recognition_enabled,
  });

  const updateLabelMutation = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) =>
      facesApi.update(id, { label }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faces'] });
      setSelectedFace(null);
      toast.success('Face label updated');
    },
    onError: () => {
      toast.error('Failed to update label');
    },
  });

  const deleteFaceMutation = useMutation({
    mutationFn: (id: string) => facesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faces'] });
      setSelectedFace(null);
      setShowDeleteConfirm(false);
      toast.success('Face deleted');
    },
    onError: () => {
      toast.error('Failed to delete face');
    },
  });

  const handleSaveLabel = () => {
    if (!selectedFace) return;
    const label = editLabel.trim() || null;
    updateLabelMutation.mutate({ id: selectedFace.id, label });
  };

  const handleOpenFace = (face: Face) => {
    setSelectedFace(face);
    setEditLabel(face.label || '');
    setShowDeleteConfirm(false);
  };

  const handleCloseFace = () => {
    setSelectedFace(null);
    setEditLabel('');
    setShowDeleteConfirm(false);
  };

  // Filter faces by search query
  const filteredFaces = faces?.filter((face) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      face.label?.toLowerCase().includes(query) ||
      face.client_face_id.toLowerCase().includes(query)
    );
  });

  // Show message if face recognition is not enabled
  if (!orgLoading && organization && !organization.face_recognition_enabled) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Faces</h1>
          <p className="text-gray-500 dark:text-slate-400">Manage detected faces and assign labels</p>
        </div>
        <Card className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Face Recognition is Disabled
          </h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4 max-w-md mx-auto">
            Enable face recognition in Settings to start identifying and labeling individuals.
          </p>
          <Button variant="outline" onClick={() => window.location.href = '/settings'}>
            Go to Settings
          </Button>
        </Card>
      </div>
    );
  }

  const isLoading = orgLoading || facesLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Faces</h1>
          <p className="text-gray-500 dark:text-slate-400">
            {faces?.length || 0} face{faces?.length !== 1 ? 's' : ''} detected
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-500" />
        <input
          type="text"
          placeholder="Search by name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Faces Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-square bg-gray-200 dark:bg-slate-700 rounded-lg mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : filteredFaces?.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchQuery ? 'No faces found' : 'No faces detected yet'}
          </h3>
          <p className="text-gray-500 dark:text-slate-400">
            {searchQuery
              ? 'Try a different search term'
              : 'Faces will appear here once detected by your cameras'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredFaces?.map((face) => (
            <button
              key={face.id}
              onClick={() => handleOpenFace(face)}
              className="text-left group"
            >
              <div className="aspect-square bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden mb-2 relative">
                {face.thumbnail ? (
                  <img
                    src={`data:image/jpeg;base64,${face.thumbnail}`}
                    alt={face.label || 'Unknown face'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-12 h-12 text-gray-400 dark:text-slate-500" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {face.label || `#${face.client_face_id.slice(-6)}`}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {formatRelativeTime(face.last_seen)}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Face Detail Modal */}
      {selectedFace && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md relative">
            <button
              onClick={handleCloseFace}
              className="absolute top-4 right-4 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              {/* Face Image */}
              <div className="aspect-square max-w-[200px] mx-auto bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                {selectedFace.thumbnail ? (
                  <img
                    src={`data:image/jpeg;base64,${selectedFace.thumbnail}`}
                    alt={selectedFace.label || 'Unknown face'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-16 h-16 text-gray-400 dark:text-slate-500" />
                  </div>
                )}
              </div>

              {/* Face Info */}
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  ID: #{selectedFace.client_face_id.slice(-6)}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Seen {selectedFace.sample_count} time{selectedFace.sample_count !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Label Input */}
              <Input
                label="Name / Label"
                placeholder="Enter a name for this person..."
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
              />

              {/* Metadata */}
              <div className="text-sm text-gray-500 dark:text-slate-400 space-y-1">
                <p>First seen: {formatDate(selectedFace.first_seen)}</p>
                <p>Last seen: {formatDate(selectedFace.last_seen)}</p>
              </div>

              {/* Actions */}
              {!showDeleteConfirm ? (
                <div className="flex gap-3">
                  <Button
                    onClick={handleSaveLabel}
                    isLoading={updateLabelMutation.isPending}
                    className="flex-1"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Save Label
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg space-y-3">
                  <p className="text-sm text-red-800 dark:text-red-300">
                    Delete this face? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="danger"
                      onClick={() => deleteFaceMutation.mutate(selectedFace.id)}
                      isLoading={deleteFaceMutation.isPending}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
